import {
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ICreatePosition,
  IDepartment,
  IDepartmentPosition,
  IPosition,
  IUpdatePosition,
  IUsersInDepartment,
} from "../models/Department";
import { departmentService } from "../services/departmentService";
import { departmentQueryKey } from "./QueryKey";

export function useGetDepartments(): UseQueryResult<IDepartment[]> {
  return useQuery({
    queryKey: [departmentQueryKey.DEPARTMENTS],
    queryFn: departmentService.getAllDepartments,
  });
}
export function useGetDepartmentById(
  id: number
): UseQueryResult<IDepartmentPosition | undefined> {
  return useQuery({
    queryKey: [departmentQueryKey.DEPARTMENT, id],
    queryFn: () => departmentService.getDepartmentById(id),
    enabled: !!id,
  });
}
export function useGetUserByDepartmentCode(
  departmentCode: string
): UseQueryResult<IUsersInDepartment[]> {
  return useQuery({
    queryKey: [departmentQueryKey.DEPARTMENT, departmentCode, "user"],
    queryFn: () => departmentService.getUserByDepartmentId(departmentCode),
    enabled: !!departmentCode,
  });
}
export function useUpdatePosition(): UseMutationResult<
  IPosition,
  Error,
  IUpdatePosition,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (position: IUpdatePosition) =>
      departmentService.updatePosition(position),
    onSuccess: (updatedPosition: IPosition) => {
      queryClient.setQueryData<IDepartmentPosition>(
        [departmentQueryKey.DEPARTMENT, updatedPosition.departmentId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            positions: oldData.positions.map((pos) =>
              pos.id === updatedPosition.id ? updatedPosition : pos
            ),
          };
        }
      );
    },
  });
}
export function useDeletePosition(): UseMutationResult<
  void,
  Error,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => departmentService.deletePosition(id),
    onSuccess: (departmentId, positionId) => {
      queryClient.setQueryData<IDepartmentPosition>(
        [departmentQueryKey.DEPARTMENT, departmentId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            positions: oldData.positions.filter((pos) => pos.id !== positionId),
          };
        }
      );
    },
  });
}

export function useCreatePosition(): UseMutationResult<
  IPosition,
  Error,
  ICreatePosition & { departmentId: number },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (position: ICreatePosition & { departmentId: number }) =>
      departmentService.createPosition(position),
    onSuccess: (newPosition: IPosition) => {
      queryClient.setQueryData<IDepartmentPosition>(
        [departmentQueryKey.DEPARTMENT, newPosition.departmentId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            positions: [...oldData.positions, newPosition],
          };
        }
      );
    },
  });
}
